import { useState, useRef, useEffect } from 'react'
import { Shield, Check, ArrowRight, Zap, Star, Building2, X, Loader2, Phone, MessageCircle, Instagram, Music, Info } from 'lucide-react'
import { paymentsApi, subscriptionsApi } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { PLANS } from '../data/pricing'

const ICONS = { Zap, Star, Building2 }

function getPlanRank(planId) {
  // ✅ Safety check - if PLANS is undefined, return -1
  if (!PLANS || !Array.isArray(PLANS)) return -1
  return PLANS.findIndex((p) => p.id === planId)
}

function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true)
    }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, inView]
}

function FadeIn({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>{children}</div>
  )
}

const FAQ = [
  { q: 'What does my subscription cover?', a: 'Your monthly subscription gives you access to the Bizos Suite platform — your document vault, staff management, governance scoring, investor portal, and audit trail. It also unlocks specific document categories based on your plan: Seed unlocks Corporate & Structure and Core Financials; Growth adds Team & Human Capital; Enterprise adds Risk, Compliance & ESG and Legal & Protection.' },
  { q: 'Do I need to pay for documents separately?', a: 'Some documents — like those in categories not included in your plan, or specialized compliance filings — may require a separate one-time payment. You can also request an agent to help you obtain missing documents. Any document you already have can be uploaded and stored at no extra cost.' },
  { q: 'Can I change plans anytime?', a: 'Yes. Upgrade or downgrade at any time. Changes take effect immediately and we prorate the difference. Only founders are billed — staff and investors use the platform at no cost.' },
  { q: 'What payment methods do you accept?', a: 'We accept all major cards through Paystack — Visa, Mastercard, Verve, and local Nigerian bank transfers.' },
  { q: 'Is my data secure?', a: 'Absolutely. All documents are encrypted at rest and in transit. Row-level security ensures only authorized users can access your data. Every action is logged in an immutable audit trail.' },
  { q: 'Do you offer discounts for nonprofits?', a: 'Yes — registered nonprofits and social enterprises get 30% off any plan. Contact us to learn more.' },
]

export default function Pricing({ onNavigate, onGetStarted }) {
  const { user, company } = useAuth()
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [checkoutPlan, setCheckoutPlan] = useState(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState(null)
  const [openFaq, setOpenFaq] = useState(null)
  const [currentPlan, setCurrentPlan] = useState(null)

  // ✅ Safety check - if PLANS is undefined or empty, use empty array
  const plans = PLANS && Array.isArray(PLANS) ? PLANS : []

  useEffect(() => {
    if (!company?.id) return
    let mounted = true
    ;(async () => {
      const { data: sub } = await subscriptionsApi.get(company.id)
      if (!mounted) return
      if (sub?.plan) setCurrentPlan(sub.plan)
    })()
    return () => { mounted = false }
  }, [company?.id])

  const handleSubscribe = async (planId) => {
    if (!user) {
      onGetStarted()
      return
    }
    setCheckoutPlan(planId)
    setCheckoutLoading(true)
    setCheckoutError(null)

    const companyId = company?.id
    if (!companyId) {
      setCheckoutError('No company profile found. Please complete onboarding first.')
      setCheckoutLoading(false)
      return
    }

    const callbackUrl = window.location.origin + '?view=pricing&payment=return'
    const { data, error } = await paymentsApi.initialize(planId, companyId, callbackUrl)

    if (error) {
      setCheckoutError(error.message || error.error || 'Failed to initialize payment')
      setCheckoutLoading(false)
      return
    }

    if (data?.authorization_url) {
      window.location.href = data.authorization_url
    } else {
      setCheckoutError('No payment URL returned. Please ensure Paystack is configured.')
      setCheckoutLoading(false)
    }
  }

  // ✅ If no plans, show loading or error
  if (plans.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-brand mx-auto" />
          <p className="mt-4 text-muted">Loading pricing plans...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg text-ink">Bizos Suite</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => onNavigate('landing')} className="text-sm font-medium text-neutral-700 hover:text-brand">Home</button>
            <button onClick={() => onNavigate('about')} className="text-sm font-medium text-neutral-700 hover:text-brand">About</button>
            <button onClick={() => onNavigate('contact')} className="text-sm font-medium text-neutral-700 hover:text-brand">Contact</button>
            <button onClick={onGetStarted} className="text-sm font-semibold text-brand">Sign In</button>
            <button onClick={() => onNavigate('pricing')} className="btn-primary text-sm">Get Started</button>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-12 px-6 bg-gradient-to-br from-neutral-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-brand-soft text-brand text-xs font-semibold rounded-full mb-6">
              Pricing
            </span>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight">Simple, transparent pricing</h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-lg text-neutral-600">Choose the plan that fits your stage. Pay securely with Paystack — cards, bank transfer, or USSD.</p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="mt-8 inline-flex items-center gap-1 p-1 bg-neutral-100 rounded-lg">
              <button onClick={() => setBillingCycle('monthly')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${billingCycle === 'monthly' ? 'bg-white text-ink shadow-sm' : 'text-neutral-500'}`}>Monthly</button>
              <button onClick={() => setBillingCycle('yearly')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${billingCycle === 'yearly' ? 'bg-white text-ink shadow-sm' : 'text-neutral-500'}`}>Yearly <span className="text-xs text-emerald-600 ml-1">Save 20%</span></button>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          {plans.map((plan, i) => {
            // ✅ Safety check for icon
            const Icon = ICONS[plan.icon] || Star
            const isCurrent = user && currentPlan === plan.id
            return (
              <FadeIn key={plan.id} delay={i * 0.1}>
                <div className={`relative p-8 rounded-2xl border-2 transition-all ${plan.highlighted ? 'border-brand shadow-xl scale-105' : 'border-neutral-200 hover:border-neutral-300'}`}>
                  {plan.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand text-white text-xs font-bold rounded-full">MOST POPULAR</span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${plan.highlighted ? 'bg-brand' : 'bg-ink'}`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-ink">{plan.name}</h3>
                  </div>
                  <p className="text-sm text-neutral-500 mb-6">{plan.tagline}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-ink">₦{plan.price.toLocaleString()}</span>
                    <span className="text-neutral-400">/{plan.period}</span>
                  </div>
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={user && isCurrent}
                    className={`w-full py-3 rounded-lg font-semibold text-sm transition-all ${
                      user && isCurrent
                        ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed border border-neutral-200'
                        : plan.highlighted
                          ? 'btn-primary'
                          : 'btn-secondary'
                    }`}
                  >
                    {!user
                      ? 'Start Free Trial'
                      : isCurrent
                        ? 'Current Plan'
                        : getPlanRank(plan.id) < getPlanRank(currentPlan)
                          ? 'Downgrade'
                          : 'Upgrade'}
                  </button>
                  <div className="mt-8 space-y-3">
                    {/* ✅ Safety check for features */}
                    {plan.features && plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-2">
                        <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                        <span className="text-sm text-neutral-700">{feature}</span>
                      </div>
                    ))}
                    {/* ✅ Safety check for notIncluded */}
                    {plan.notIncluded && plan.notIncluded.map((feature) => (
                      <div key={feature} className="flex items-start gap-2 opacity-50">
                        <X size={16} className="text-neutral-400 shrink-0 mt-0.5" />
                        <span className="text-sm text-neutral-400 line-through">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            )
          })}
        </div>
      </section>

      {/* Subscription Clarification */}
      <section className="pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <div className="p-6 rounded-xl bg-blue-50 border border-blue-200 flex items-start gap-4">
              <Info size={20} className="text-blue-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-ink mb-1">What your subscription covers</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  Your monthly subscription pays for access to the Bizos Suite platform — your document vault, staff management, governance scoring, investor portal, and audit trail. It also unlocks specific document categories based on your plan. Documents you already have can be uploaded and stored at no extra cost. Some specialized documents or compliance filings may require a separate one-time payment if they fall outside your plan's included categories.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <div className="p-8 rounded-2xl bg-neutral-50 border border-neutral-200">
              <h3 className="text-lg font-semibold text-ink mb-2">Secure payments powered by Paystack</h3>
              <p className="text-sm text-neutral-500 mb-4">We accept all major payment methods available in Nigeria</p>
              <div className="flex flex-wrap justify-center gap-3">
                {['Visa', 'Mastercard', 'Verve', 'Bank Transfer', 'USSD'].map((method) => (
                  <span key={method} className="px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-medium text-neutral-700">{method}</span>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24 px-6 bg-neutral-50">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-3xl font-bold text-ink tracking-tight text-center mb-12">Frequently Asked Questions</h2>
          </FadeIn>
          <div className="space-y-4">
            {FAQ.map((item, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="p-5 rounded-xl bg-white border border-neutral-200">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between text-left">
                    <span className="text-sm font-semibold text-ink">{item.q}</span>
                    <span className={`text-neutral-400 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openFaq === i && (
                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{item.a}</p>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Checkout Modal */}
      {checkoutPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => !checkoutLoading && setCheckoutPlan(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
            {checkoutLoading ? (
              <>
                <Loader2 size={32} className="animate-spin text-brand mx-auto mb-4" />
                <h3 className="text-lg font-bold text-ink">Initializing payment...</h3>
                <p className="mt-2 text-sm text-neutral-500">Redirecting to Paystack secure checkout</p>
              </>
            ) : checkoutError ? (
              <>
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X size={24} className="text-rose-600" />
                </div>
                <h3 className="text-lg font-bold text-ink">Payment Error</h3>
                <p className="mt-2 text-sm text-neutral-600">{checkoutError}</p>
                <p className="mt-2 text-xs text-neutral-400">Make sure your Paystack key is configured in project settings.</p>
                <button onClick={() => setCheckoutPlan(null)} className="btn-secondary mt-6">Close</button>
              </>
            ) : null}
          </div>
        </div>
      )}

      <footer className="py-12 px-6 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-ink">Bizos Suite</span>
          </div>
          <div className="flex flex-wrap gap-4 mb-4">
            <a href="tel:08142243764" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Phone size={14} /> 08142243764
            </a>
            <a href="https://wa.me/2348142243764" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <MessageCircle size={14} /> WhatsApp
            </a>
            <a href="https://instagram.com/officialbizos" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Instagram size={14} /> @officialbizos
            </a>
            <a href="https://tiktok.com/@officialbizos" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Music size={14} /> @officialbizos
            </a>
          </div>
          <p className="text-xs text-neutral-400">© 2026 Bizos Suite. All rights reserved. Built in Nigeria for Africa. Powered by Vobels Limited.</p>
        </div>
      </footer>
    </div>
  )
}